<script setup lang="ts">
import { computed, ref } from 'vue'
import BKButton from '@/components/AppShared/BKButton.vue'
import { IEvent } from '@/enitites/event'
import ToggleLike from '@/features/toggle-like/ui/ToggleLike.vue'
import { IEventFeedback, IEventFeedbackPostData } from '@/enitites/event/model/types'
import { useRouter } from 'vue-router/composables'
import { useEventFeedbackMutation } from '@/enitites/event/api/queries'
import { EVENT_STAR_CONFIG } from '../constants'

const props = defineProps<{
  setFeedback: (feedback: IEventFeedbackPostData) => void
  event: IEvent
  feedback: IEventFeedback | null
}>()

const router = useRouter()
const { mutateAsync, isPending } = useEventFeedbackMutation(props.event.id)

const isUpdate = computed(() => props.feedback)

const feedbackText = ref(props.feedback?.details || '')
const showValidationError = ref(false)

const ratingCategories = ref([
  { key: 'timeliness', label: 'Timeliness', value: props.feedback?.timeliness || 0 },
  { key: 'communication', label: 'Communication', value: props.feedback?.communication || 0 },
  { key: 'venue', label: 'Venue', value: props.feedback?.venue || 0 },
  { key: 'vibe', label: 'Vibe', value: props.feedback?.vibe || 0 },
  { key: 'musicSound', label: 'Music / Sound', value: props.feedback?.musicSound || 0 },
])

const isFormValid = computed(() => {
  return ratingCategories.value.every((category) => category.value > 0)
})

function handleFeedbackClose() {
  router.push({
    name: 'Event',
    params: { id: `${props.event?.id}` },
    query: { tab: 'info' },
  })
}

const handleSubmitFeedback = async () => {
  if (!isFormValid.value) {
    showValidationError.value = true
    return
  }

  showValidationError.value = false

  const ratings = Object.fromEntries(ratingCategories.value.map((cat) => [cat.key, cat.value])) as {
    timeliness: number
    communication: number
    venue: number
    vibe: number
    musicSound: number
  }

  const averageRating = (
    ratingCategories.value.reduce((sum, cat) => sum + cat.value, 0) / ratingCategories.value.length
  ).toFixed(2)

  const data: IEventFeedbackPostData = {
    averageRating: parseFloat(averageRating),
    timeliness: ratings.timeliness,
    communication: ratings.communication,
    musicSound: ratings.musicSound,
    venue: ratings.venue,
    vibe: ratings.vibe,
    details: feedbackText.value.trim(),
  }

  await mutateAsync({ data, isUpdate: !!isUpdate.value })
  handleFeedbackClose()
  props.setFeedback(data)
}

const handleRatingChange = () => {
  if (showValidationError.value && isFormValid.value) {
    showValidationError.value = false
  }
}
</script>

<template>
  <div class="wrapper">
    <div class="section">
      <div class="title">Did you enjoy this event?</div>
      <ToggleLike
        :is-liked="event.score.isLiked"
        color-svg="#000"
        :event-id="event.id"
        @update:is-liked="(newState) => (event.score.isLiked = newState)"
      />
    </div>

    <div class="section">
      <div class="title">Your feedback will be visible only to the organizer of the event.</div>
      <div
        v-if="showValidationError"
        class="validation-error"
      >
        Please rate all categories before submitting your feedback.
      </div>

      <div class="items">
        <div
          v-for="category in ratingCategories"
          :key="category.key"
          class="item"
          :class="{ invalid: showValidationError && category.value === 0 }"
        >
          <v-rating
            v-bind="EVENT_STAR_CONFIG"
            v-model="category.value"
            size="27"
            @update:model-value="handleRatingChange"
          />
          <div>{{ category.label }}</div>
        </div>
      </div>
    </div>

    <div class="section">
      <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -->
      <textarea
        v-model="feedbackText"
        class="textarea"
        placeholder="Add any additional comments..."
      />
    </div>

    <div class="buttons">
      <BKButton
        :outlined="true"
        width="100%"
        :disabled="isPending"
        @click="handleFeedbackClose"
      >
        Cancel
      </BKButton>
      <BKButton
        :white-text="true"
        width="100%"
        :disabled="isPending"
        @click="handleSubmitFeedback"
      >
        {{ isUpdate ? 'Update feedback' : 'Submit feedback' }}
      </BKButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/mixins';

.wrapper {
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 865px;
  margin: 35px auto;

  @media (max-width: 920px) {
    width: 90%;
  }

  .star {
    gap: 5px;

    :deep(button) {
      padding: 0;
    }
  }

  .section {
    .like {
      display: flex;
      gap: 8px;
      align-items: center;
    }

    .title {
      @include font-h5;

      margin-bottom: 8px;
      color: #000;
      font-weight: 600;

      &.regular {
        font-weight: 400;
      }
    }

    .validation-error {
      margin-bottom: 16px;
      padding: 12px;
      color: #c62828;
      font-weight: 500;
      background: #ffebee;
      border: 1px solid #ef9a9a;
      border-radius: 6px;

      @include font-body2;
    }

    .items {
      display: flex;
      flex-direction: column;
      gap: 20px;

      .item {
        display: flex;
        gap: 10px;
        align-items: center;
        padding: 8px;
        border-radius: 6px;
        transition: background-color 0.2s ease;

        @include font-body2;

        &.invalid {
          background: #ffebee;
          border: 1px solid #ef9a9a;
        }
      }
    }
  }

  .textarea {
    width: 100%;
    min-height: 170px;
    padding: 8px 12px;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 6px;

    &::placeholder {
      color: #999;
    }
  }

  .buttons {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }
}
</style>
